/**
 * Ground War outcome mapping (ARCHITECTURE 3.5) and the star/crown keys in profile.stats.campaignStars.
 * Stars live under `gw:<levelId>`, Hard crowns under `gwh:<levelId>`; neither counts toward the artillery totals.
 */
import type { PlayerOutcome, PlayerStats } from '../types';
import { GW_TICK_HZ, type GwDifficulty, type GwSide } from './state';
import type { GwVariant } from './settings';

export const GW_STAR_PREFIX = 'gw:';
export const GW_CROWN_PREFIX = 'gwh:';
/** The 26 campaign level ids (a hard-coded copy of spec.campaign.levels[].id; gw-spec.test.ts checks it). */
export const GW_LEVEL_IDS: readonly string[] = [
  ...[1, 2, 3, 4, 5].flatMap((r) => [1, 2, 3, 4, 5].map((l) => `r${r}l${l}`)),
  'final',
];
/** Stars per level. */
export const GW_STARS_MAX = 3;
/** Ticks per battle minute (PlayerOutcome.roundsPlayed counts whole minutes). */
export const GW_TICKS_PER_MINUTE = GW_TICK_HZ * 60;

export function gwStarKey(levelId: string): string {
  return GW_STAR_PREFIX + levelId;
}
export function gwCrownKey(levelId: string): string {
  return GW_CROWN_PREFIX + levelId;
}
/** Exactly the `gw:` prefix (a `gwh:` crown key is not a star key). */
export function isGwStarKey(key: string): boolean {
  return key.startsWith(GW_STAR_PREFIX);
}
/** Either Ground War key (stars or crowns); these stay out of the artillery star totals. */
export function isGwKey(key: string): boolean {
  return /^gwh?:/.test(key);
}

function clampStars(v: unknown): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(GW_STARS_MAX, Math.floor(v)));
}

/** Ground War stars over the 26 valid level ids, each clamped 0..3 (≤ 78); crowns never count. */
export function gwStarsTotal(stats: PlayerStats): number {
  let n = 0;
  for (const id of GW_LEVEL_IDS) n += clampStars(stats.campaignStars[gwStarKey(id)]);
  return n;
}

/** Hard crowns over the 26 valid level ids. */
export function gwCrownsTotal(stats: PlayerStats): number {
  let n = 0;
  for (const id of GW_LEVEL_IDS) if ((stats.campaignStars[gwCrownKey(id)] ?? 0) >= 1) n++;
  return n;
}

/** Result of evalStars (client/src/gw/sim/script.ts). */
export interface GwStarResult {
  win: boolean;
  par: boolean;
  bonus: boolean;
  stars: 0 | 1 | 2 | 3;
}

export interface GwOutcomeInput {
  playerId: string;
  /** The side the player commands. */
  side: GwSide;
  /** GwState.winner. */
  winner: GwSide | -1;
  /** Ticks the battle lasted (GwState.endTick). */
  endTick: number;
  variant: GwVariant;
  difficulty: GwDifficulty;
  /** settings.gw.levelId (campaign, or co-op on a level). */
  levelId?: string;
  /** From evalStars; ignored on a loss. */
  stars?: GwStarResult;
}

/**
 * The artillery PlayerOutcome a Ground War battle reports (3.5): no flawless, domination or comeback data;
 * roundsPlayed = whole battle minutes, parRounds 0; `campaign` exactly when a levelId is set (losses too, stars 0).
 */
export function gwPlayerOutcome(input: GwOutcomeInput): PlayerOutcome {
  const win = input.winner !== -1 && input.winner === input.side;
  const out: PlayerOutcome = {
    playerId: input.playerId,
    result: win ? 'win' : 'loss',
    unitsStart: 0,
    unitsAlive: 0,
    hpStart: 0,
    hpRemaining: 0,
    roundsPlayed: Math.max(0, Math.floor(input.endTick / GW_TICKS_PER_MINUTE)),
    parRounds: 0,
    lastStand: false,
  };
  if (input.levelId) {
    const stars = win ? (clampStars(input.stars?.stars ?? 1) as 0 | 1 | 2 | 3) : 0;
    out.campaign = { levelId: gwStarKey(input.levelId), stars };
    if (win && input.difficulty === 'hard' && input.variant !== 'pvp') out.campaign.crown = true;
  }
  return out;
}
